import { SensorFormValues, widthDecimals } from '@moduleAForm';
import {
	DeviceMakeModelId,
	DeviceMakeModelItem,
	MDComponent,
	UpdateAction,
	UpdateMDComponent,
} from '@types';
import { isValidUUID } from '@utils';

import { Mapper } from './Mapper';
import { ReadingMapper } from './ReadingMapper';

/**
 * Base MDComponent type to hold the mapped formValues fields
 */
type MDComponentBase = {
	component?: DeviceMakeModelId;
	make?: string;
	model?: string;
	serialNumber?: string;
	sizeType?: MDComponent.sizeType;
	size?: number;
	width?: number;
	widthUnits?: MDComponent.widthUnits;
};

export class SensorMapper extends Mapper<SensorFormValues, MDComponent, UpdateMDComponent> {
	private makesModelsData: DeviceMakeModelItem[];
	private readingMapper = new ReadingMapper();

	constructor(makesModelsData: DeviceMakeModelItem[]) {
		super();
		this.makesModelsData = makesModelsData;
	}

	/**
	 * Map from model attributes with this preference: change > existing > new
	 */
	public mapToForm(data: MDComponent, dataSource?: 'new' | 'changeExisting'): SensorFormValues {
		let identifiedMakeModel;
		let mainMappedSensor: Omit<MDComponentBase, 'component'>;
		switch (dataSource) {
			case 'changeExisting':
				identifiedMakeModel = this.findMakeModelById(data.changeComponent);
				identifiedMakeModel ??= this.findMakeModelById(data.existingComponent);
				mainMappedSensor = {
					make: data.changeMake,
					model: data.changeModel,
					serialNumber: data.changeSerialNumber ?? data.serialNumber,
					sizeType: data.changeSizeType ?? data.sizeType,
					size: data.changeSize ?? data.size,
					width: data.changeWidth ?? data.width,
					widthUnits: data.changeWidthUnits ?? data.widthUnits,
				};
				break;
			case 'new':
				identifiedMakeModel = this.findMakeModelById(data.newComponent);
				mainMappedSensor = {
					make: data.newMake,
					model: data.newModel,
					serialNumber: data.newSerialNumber,
					sizeType: data.newSizeType,
					size: data.newSize,
					width: data.newWidth,
					widthUnits: data.newWidthUnits,
				};
				break;
			default:
				mainMappedSensor = {};
				break;
		}

		const { make, model } = identifiedMakeModel ?? {};
		const identifiedMakeModelName = make && model ? `Sensor: Make - ${make}: Model - ${model}` : undefined; // prettier-ignore

		const mappedReading = this.readingMapper.mapToForm(data.reading ?? {});

		return {
			id: data.id,
			action: UpdateAction.NONE,
			field2: identifiedMakeModelName as unknown as string,
			field3: mainMappedSensor.make as unknown as string,
			field4: mainMappedSensor.model as unknown as string,
			field5: mainMappedSensor.serialNumber ?? '',
			field6: mappedReading.readingValue,
			field7: mappedReading.readingUnit,
			field8: mainMappedSensor.sizeType as unknown as MDComponent.sizeType,
			field9: mainMappedSensor.size,
			field10: this.formatNumber(mainMappedSensor.width, widthDecimals),
			field11: mainMappedSensor.widthUnits as unknown as MDComponent.widthUnits,
		};
	}

	public mapToFormList(
		data: MDComponent[],
		dataSource?: 'new' | 'changeExisting',
	): SensorFormValues[] {
		const dataFilteredBySource = data.filter(item => {
			const hasChangeComponent = isValidUUID(item.changeComponent);
			const hasExistingComponent = isValidUUID(item.existingComponent);
			const isFromExistingSource = hasChangeComponent || hasExistingComponent;
			const isFromNewSource = isValidUUID(item.newComponent);
			switch (dataSource) {
				case 'changeExisting':
					return isFromExistingSource;
				case 'new':
					return !isFromExistingSource && isFromNewSource; // mutual exclusion by preference changeExisting over new
				default:
					return false;
			}
		});
		return dataFilteredBySource.map(item => this.mapToForm(item, dataSource));
	}

	public mapFormToPayload(
		formValues: SensorFormValues,
		dataTarget?: 'new' | 'changeExisting',
	): UpdateMDComponent {
		const identifiedMakeModel = this.findMakeModelByName(formValues.field2);

		const mainMappedSensor: MDComponentBase = {
			component: identifiedMakeModel?.id,
			make: formValues.field3,
			model: formValues.field4,
			serialNumber: formValues.field5,
			sizeType: formValues.field8
				? this.mapStringToEnum(formValues.field8, MDComponent.sizeType)
				: undefined,
			size: formValues.field9,
			width: formValues.field10?.length ? Number(formValues.field10) : undefined,
			widthUnits: formValues.field11
				? this.mapStringToEnum(formValues.field11, MDComponent.widthUnits)
				: undefined,
		};

		let mainMappedSensorToTarget: Partial<MDComponent>;
		switch (dataTarget) {
			case 'changeExisting':
				mainMappedSensorToTarget = {
					changeComponent: mainMappedSensor.component,
					changeMake: mainMappedSensor.make,
					changeModel: mainMappedSensor.model,
					changeSerialNumber: mainMappedSensor.serialNumber,
					changeSizeType: mainMappedSensor.sizeType,
					changeSize: mainMappedSensor.size,
					changeWidth: mainMappedSensor.width,
					changeWidthUnits: mainMappedSensor.widthUnits,
				};
				break;
			case 'new':
				mainMappedSensorToTarget = {
					newComponent: mainMappedSensor.component,
					newMake: mainMappedSensor.make,
					newModel: mainMappedSensor.model,
					newSerialNumber: mainMappedSensor.serialNumber,
					newSizeType: mainMappedSensor.sizeType,
					newSize: mainMappedSensor.size,
					newWidth: mainMappedSensor.width,
					newWidthUnits: mainMappedSensor.widthUnits,
				};
				break;
			default:
				mainMappedSensorToTarget = {};
				break;
		}

		return {
			MDComponent: {
				id: formValues.id,
				...mainMappedSensorToTarget,
				reading: this.readingMapper.mapFormToPayload({
					readingValue: formValues.field6,
					readingUnit: formValues.field7,
				}),
			},
			action: formValues.action,
		};
	}

	public mapFormToPayloadList(
		formValues: SensorFormValues[],
		dataTarget?: 'new' | 'changeExisting',
	): Partial<UpdateMDComponent>[] {
		return formValues.map(item => this.mapFormToPayload(item, dataTarget));
	}

	public mapModelToPayload(data: MDComponent): UpdateMDComponent {
		return {
			MDComponent: data,
			action: UpdateAction.NONE,
		};
	}

	public extractMakeModel(mdComponent: MDComponent) {
		const makeModelId = mdComponent.existingComponent;
		return this.findMakeModelById(makeModelId);
	}

	private findMakeModelById(makeModelId?: DeviceMakeModelId) {
		if (makeModelId) {
			return this.makesModelsData.find(item => item.id === makeModelId);
		}
	}

	private findMakeModelByName(makeModel: string) {
		const { make, model } = this.extractMakeAndModel(makeModel);
		return this.makesModelsData.find(item => {
			const isSensor = item.deviceType === DeviceMakeModelItem.deviceType.SENSOR;
			const isMatchByMake = item.make === make;
			const isMatchByModel = item.model === model;
			return isSensor && isMatchByMake && isMatchByModel;
		});
	}

	/**
	 * Extracts the make and model from the given field value
	 * @param {string} makeModel - The field value containing the make and model
	 * @return {{ make: string; model: string }} - An object with the extracted make and model
	 */
	private extractMakeAndModel(makeModel: string): {
		make: string;
		model: string;
	} {
		const sensorMakeAndModelRegex = /Sensor: Make - ([^:]*): Model - ([^:]*)/;
		const sensorMakeAndModelMatch = sensorMakeAndModelRegex.exec(makeModel);
		const make = sensorMakeAndModelMatch ? sensorMakeAndModelMatch[1] : '';
		const model = sensorMakeAndModelMatch ? sensorMakeAndModelMatch[2] : '';
		return { make, model };
	}
}
